import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { DirectoryRecord, FileRecord } from "@/shared/domain/files";
import {
  DirectoryNode,
  FileNode,
  FileSystemNode,
  FileSystemNodeType,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import { MoveNodeOperation } from "@/shared/domain/files/operations";

import { fileQueryKeys } from "./fileQueryKeys";
import {
  cancelAndSnapshotDirectoryContents,
  DirectoryContentsCache,
  restoreDirectoryContentsSnapshot,
  transformDirectoryContentsCache,
} from "./moveDirectoryContentsCache";

type MoveContext = {
  snapshot: [QueryKey, DirectoryContentsCache | undefined][];
};

type BulkMoveNodesMutationArgs = {
  datasetId: string;
  operations: MoveNodeOperation[];
  /** The FileSystemNode objects being moved — used for optimistic cache updates. */
  nodes: FileSystemNode[];
  /** Relative path of the directory the nodes are moving out of. */
  sourcePath: string;
  /** Relative path of the directory the nodes are moving into. */
  destinationPath: string;
};

export function useBulkMoveNodesMutation() {
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    (FileRecord | DirectoryRecord)[],
    Error,
    BulkMoveNodesMutationArgs,
    MoveContext
  >({
    mutationFn: ({ datasetId, operations }) => {
      return fileService.bulkMoveNodes(datasetId, operations);
    },
    onMutate: async ({ datasetId, nodes, sourcePath, destinationPath }) => {
      const snapshot = await cancelAndSnapshotDirectoryContents(
        queryClient,
        datasetId,
        [sourcePath, destinationPath],
      );

      // Build a set of node IDs for fast membership check
      const nodeIds = new Set<string>();
      for (const n of nodes) {
        if (isFile(n)) {
          nodeIds.add(n.file.file_id);
        } else if (isDirectory(n)) {
          nodeIds.add(n.directory.directory_id);
        }
      }

      transformDirectoryContentsCache(
        queryClient,
        datasetId,
        sourcePath,
        (items) =>
          items.filter((n) => {
            if (isFile(n)) {
              return !nodeIds.has(n.file.file_id);
            }
            if (isDirectory(n)) {
              return !nodeIds.has(n.directory.directory_id);
            }
            return true;
          }),
      );

      if (sourcePath !== destinationPath) {
        const optimisticNodes = nodes.flatMap<FileSystemNode>((n) => {
          const newPath = destinationPath
            ? `${destinationPath}/${n.name}`
            : n.name;
          if (isFile(n)) {
            return [
              {
                type: FileSystemNodeType.File,
                name: n.name,
                file: { ...n.file, relative_path: newPath },
              } as FileNode,
            ];
          }
          if (isDirectory(n)) {
            return [
              {
                type: FileSystemNodeType.Directory,
                name: n.name,
                directory: { ...n.directory, relative_path: newPath },
              } as DirectoryNode,
            ];
          }
          return [];
        });

        transformDirectoryContentsCache(
          queryClient,
          datasetId,
          destinationPath,
          (items) => [...items, ...optimisticNodes],
        );
      }

      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        restoreDirectoryContentsSnapshot(queryClient, context.snapshot);
      }
    },
    onSettled: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.directoryItemCount(),
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.directoryContents(),
          exact: false,
        }),
      ]);
    },
  });
}
